import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Icon } from '../components/SvgIcons';
import './Home.css';

const Home = () => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [pipelineVisible, setPipelineVisible] = useState(false);
  const pipelineRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    if (user) {
      const rolePaths = {
        examiner: '/examiner',
        subject_matter_expert: '/subject-matter-expert',
        moderator: '/moderator',
        super_admin: '/super-admin',
        university_admin: '/university-admin',
        coe: '/coe-dashboard',
      };
      navigate(rolePaths[user.role] || '/examiner');
    }
  }, [user, navigate]);

  useEffect(() => {
    const el = pipelineRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPipelineVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const workflowSteps = [
    { step: '01', label: 'Template Setup', desc: 'Create paper templates manually or extract them from an existing question paper PDF using AI.' },
    { step: '02', label: 'AI Question Generation', desc: 'Examiners generate questions, sub-questions, and variations for their assigned subjects using the approved template.' },
    { step: '03', label: 'SME Review', desc: 'Subject Matter Experts review, approve, or reject submitted questions and variations.' },
    { step: '04', label: 'Moderation', desc: 'Moderators categorize approved questions and build unique paper sets.' },
    { step: '05', label: 'University Pool', desc: 'Certified faculty contribute to an anonymized university-level pool, reviewed by SMEs and Moderators.' },
    { step: '06', label: 'COE Finalization', desc: 'The Controller of Examinations selects and finalizes papers for secure, authorized download.' },
  ];

  const featureCards = [
    { icon: 'FileText', title: 'Paper Templates', description: 'Define paper structure, sections, and marks distribution manually, or upload a PDF and let AI extract the template. Templates can be shared or kept private.' },
    { icon: 'Sparkles', title: 'AI Question Generation', description: 'Generate questions, sub-questions, and multiple variations with AI, guided by the template and uploaded syllabus PDFs.' },
    { icon: 'SME', title: 'SME Review', description: 'Subject Matter Experts review questions and variations for accuracy before they move forward in the workflow.' },
    { icon: 'Moderator', title: 'Moderation & Paper Sets', description: 'Moderators categorize approved questions and generate unique paper sets from the reviewed question bank.' },
    { icon: 'GraduationCap', title: 'University Pool', description: 'University-certified examiners, SMEs, and moderators collaborate on an anonymized pool, with final paper selection by the COE.' },
    { icon: 'Lock', title: 'Secure Access & Audit', description: 'Role-based access for every stage, optional two-factor authentication, finalized paper protection, and an audit log of key actions.' },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            AI-Assisted Question Paper Generator
          </div>
          <h1 className="hero-title">
            <span className="hero-title-accent">Question Paper Generation</span>
          </h1>
          <p className="hero-subtitle">
            Create question papers from templates with AI-generated questions and variations,
            reviewed by Subject Matter Experts, organized by Moderators, and finalized by the
            Controller of Examinations.
          </p>
          <div className="hero-actions">
            <button
              className="btn btn-primary"
              onClick={() => user ? navigate('/examiner') : navigate('/login')}
            >
              Sign In
              <span className="btn-arrow">&rarr;</span>
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Features
            </button>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="pipeline" ref={pipelineRef}>
        <div className="section-header">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">From template to finalized question paper</p>
        </div>
        <div className={`pipeline-track${pipelineVisible ? ' visible' : ''}${activeStep !== null ? ' has-active' : ''}`}>
          {workflowSteps.map((step, i) => (
            <div
              key={i}
              className={`pipeline-step${pipelineVisible ? ' visible' : ''}${activeStep === i ? ' active' : ''}`}
              style={pipelineVisible ? { animationDelay: `${i * 0.15}s` } : undefined}
            >
              <div className="pipeline-milestone" onClick={() => setActiveStep(activeStep === i ? null : i)}>
                <span className="pipeline-dot">{step.step}</span>
              </div>
              <div className="pipeline-body">
                <h3 className="pipeline-label">{step.label}</h3>
                <p className="pipeline-desc">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="governance" ref={featuresRef}>
        <div className="section-header">
          <h2 className="section-title">Features</h2>
          <p className="section-subtitle">Everything needed to prepare, review, and finalize question papers</p>
        </div>
        <div className="governance-grid">
          {featureCards.map((card, i) => (
            <div key={i} className="governance-card" tabIndex={0}>
              <div className="governance-card-top">
                <span className="governance-icon-wrap">
                  <span className="governance-icon"><Icon name={card.icon} size={20} /></span>
                </span>
              </div>
              <h3 className="governance-card-title">{card.title}</h3>
              <p className="governance-card-desc">{card.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Get Started?</h2>
          <p className="cta-subtitle">Sign in to create, review, and finalize question papers</p>
          <div className="cta-actions">
            <button className="btn btn-primary" onClick={() => navigate('/login')}>
              Sign In
              <span className="btn-arrow">&rarr;</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-col">
            <h4 className="footer-col-title">Question Paper Generation</h4>
            <p className="footer-col-text">AI-assisted question paper generation with expert review, moderation, and secure finalization.</p>
          </div>
          <div className="footer-col">
            <h4 className="footer-col-title">Platform</h4>
            <ul className="footer-col-list">
              <li>Paper Templates</li>
              <li>AI Question Generation</li>
              <li>SME &amp; Moderator Review</li>
              <li>University Pool</li>
            </ul>
          </div>
          <div className="footer-col">
            <h4 className="footer-col-title">Security</h4>
            <ul className="footer-col-list">
              <li>Role-Based Access Control</li>
              <li>Two-Factor Authentication</li>
              <li>Audit Logging</li>
            </ul>
          </div>
          <div className="footer-col">
            <h4 className="footer-col-title">Support</h4>
            <ul className="footer-col-list">
              <li>
                <button className="footer-link" onClick={() => setShowPrivacy(true)}>
                  Privacy &amp; AI Disclaimer
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">&copy; {new Date().getFullYear()} Question Paper Generation. All rights reserved.</p>
        </div>
      </footer>

      {/* Privacy Modal */}
      {showPrivacy && (
        <div className="privacy-overlay" onClick={() => setShowPrivacy(false)}>
          <div className="privacy-modal" onClick={e => e.stopPropagation()}>
            <h3 className="privacy-modal-title">Privacy &amp; AI Disclaimer</h3>
            <p className="privacy-modal-text">
              This system uses Artificial Intelligence (AI) and Large Language Models (LLMs)
              to assist in generating examination questions and variations.
            </p>
            <p className="privacy-modal-text">
              <strong>Important Notice:</strong> AI-generated content may contain inaccuracies or unintended errors.
            </p>
            <ul className="privacy-modal-list">
              <li>All questions must be reviewed by the Examiner and SME.</li>
              <li>No paper is finalized without human approval.</li>
              <li>AI assists but does not replace academic judgment.</li>
            </ul>
            <p className="privacy-modal-note">By using this system, users acknowledge and accept these conditions.</p>
            <button className="btn btn-primary" onClick={() => setShowPrivacy(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
